/**
 * Example usage of the Logger service
 *
 * This file demonstrates how to use the logger throughout your application.
 * The logger will automatically detect if you're in development mode and
 * only log when appropriate.
 *
 * Development mode is detected by checking:
 * - localhost/dev hostnames
 * - debug URL parameters
 * - development ports (3000, 8080, etc.)
 * - IP addresses
 * - Debug scripts and meta tags
 */

import {
	logDebug,
	logInfo,
	logWarn,
	logError,
	logGroup,
	logGroupEnd,
	isDevelopmentMode,
	LogLevel,
	setLogLevel,
} from "./logging";

export class LoggerExample {
	constructor() {
		this.demonstrateLogging();
	}

	private demonstrateLogging(): void {
		// Check if we're in development mode
		if (isDevelopmentMode()) {
			logInfo(
				"LoggerExample",
				"Running in development mode - logging is enabled"
			);
		}

		// Basic logging with different levels
		logDebug("LoggerExample", "This is a debug message");
		logInfo("LoggerExample", "This is an info message");
		logWarn("LoggerExample", "This is a warning message");
		logError("LoggerExample", "This is an error message");

		// Logging with data objects
		logDebug("LoggerExample", "User data loaded", {
			userId: 123,
			username: "testuser",
			roles: ["user", "admin"],
		});

		// Group logging for better organization
		logGroup("LoggerExample", "Authentication Process");
		logDebug("AuthFlow", "Checking user credentials");
		logInfo("AuthFlow", "User authenticated successfully");
		logDebug("AuthFlow", "Loading user permissions");
		logGroupEnd();

		// Error logging with error object
		try {
			throw new Error("Example error for logging");
		} catch (error) {
			logError("LoggerExample", "Caught an error", error);
		}

		// Conditional logging based on log level
		setLogLevel(LogLevel.WARN); // Only show warnings and errors
		logDebug("LoggerExample", "This debug message won't show");
		logWarn("LoggerExample", "This warning will show");

		// Reset to debug level
		setLogLevel(LogLevel.DEBUG);
	}

	public exampleUsageInComponent(): void {
		// Example of how you might use logging in a component

		logGroup("MyComponent", "Button Click Handler");

		logDebug("MyComponent", "Button clicked", { buttonId: "save-btn" });

		// Simulate some processing
		logInfo("MyComponent", "Processing user input");

		// Simulate validation
		const isValid = this.validateInput();
		if (!isValid) {
			logWarn("MyComponent", "Validation failed - missing required fields");
			logGroupEnd();
			return;
		}

		logInfo("MyComponent", "Input validation passed");

		// Simulate API call
		this.saveData()
			.then(() => {
				logInfo("MyComponent", "Data saved successfully");
			})
			.catch((error) => {
				logError("MyComponent", "Failed to save data", error);
			})
			.finally(() => {
				logGroupEnd();
			});
	}

	private validateInput(): boolean {
		// Simulate validation logic
		return Math.random() > 0.5;
	}

	private async saveData(): Promise<void> {
		// Simulate async operation
		return new Promise((resolve, reject) => {
			setTimeout(() => {
				if (Math.random() > 0.3) {
					resolve();
				} else {
					reject(new Error("Simulated API error"));
				}
			}, 1000);
		});
	}
}

// Usage example:
// const example = new LoggerExample();
// example.exampleUsageInComponent();
